import { computed, observable, makeObservable, action, runInAction } from 'mobx'
import { move } from '@dnd-kit/helpers'
import Wkt from 'wicket/wicket'
import axios from 'axios'
import api from 'api.js'
import { selectionTypes } from 'constants.js'
import { geoJSONBounds, smoothFactor, mergePopulationDatas, downloadKML } from 'utils'
import RegionAnalysis from './RegionAnalysis'
import _ from 'lodash'
import { catchError } from './ErrorStore'
import { selectionStore, uiStore, dataStore, rightSidebarStore } from './UIStore'
import i18next from '../i18n'
import { defaultPolygonColor, ADDITIONAL_INFO_DEFAULT_INPUT } from '../constants'
import { debounce } from 'lodash'

// Represents a custom polygon saved to the server.
// Could be a polygon that a user drew, or created with
// a buffer around a point, or selected from a shapefile.
class Geopolygon {
  @observable targetGroupId
  @observable name
  @observable created
  @observable error
  @observable.ref geoJSON
  @observable renameForm = { active: false, name: '' }
  @observable sidebarCache = new Map()
  @observable analysis = new RegionAnalysis()
  @observable color_id
  @observable transparency
  @observable population_datas
  @observable is_owner = true
  @observable status
  @observable tag_ids
  @observable additional_info = []
  @observable notes = ''
  @observable contact_detail = { given_name: '', surname: '', email: '', address: '', country: '' }

  constructor ({ targetGroupId, polygon, name, id, created, type, color_id, transparency = 50, population_datas = [], is_owner = true, shape_status = null, tag_ids = [], additional_info = [], notes = '', contact_detail = {} }) {
    makeObservable(this)
    this.id = id
    this.created = created
    this.name = name
    this.targetGroupId = targetGroupId
    this.type = type
    this.color_id = color_id
    this.transparency = transparency
    this.population_datas = population_datas
    this.is_owner = is_owner
    this.status = shape_status
    this.tag_ids = tag_ids
    this.computeGeoJSON(polygon)
    this.additional_info = additional_info
    this.notes = notes
    this.contact_detail = { given_name: '', surname: '', email: '', phone: '', address: '', country: '', ...contact_detail }
    this.analysis.load(this.pickPopulationDataForCurrentYear(population_datas), this.geoJSON)
  }

  pickPopulationDataForCurrentYear (population_datas) {
    if (Array.isArray(population_datas)) {
      return population_datas[0]
    }
    if (!population_datas) return null
    const currentYearKey = String(dataStore.defaultYear.result)
    const currentYearEntry = population_datas[currentYearKey]
    const ds = dataStore.defaultDsName || 'landscan'
    if (currentYearEntry && Array.isArray(currentYearEntry)) {
      const match = currentYearEntry.find(d => d.type === ds)
      if (match) return match
      if (currentYearEntry[0]) return currentYearEntry[0]
    }
    const firstNonEmpty = Object.values(population_datas).find(v => v && Array.isArray(v) && v.length > 0)
    if (firstNonEmpty) {
      const match = firstNonEmpty.find(d => d.type === ds)
      if (match) return match
      return firstNonEmpty[0]
    }
    return null
  }

  getTypeLabel () {
    return 'Shape'
  }

  findAdditionalInfoIndex (id) {
    return this.additional_info.findIndex(item => Number(item.info_item) === Number(id))
  }

  computeGeoJSON (polygon) {
    const wkt = new Wkt.Wkt()
    this.geoJSON = wkt.read(polygon).toJson()
  }

  @action startRename () {
    this.error = null
    this.renameForm = { active: true, name: this.name }
  }

  @action endRename () {
    this.renameForm = { active: false, name: '' }
  }

  @catchError async addAdditionalInfoItem (item) {
    runInAction(() => {
      this.additional_info.push(item)
    })
    await this.saveAdditionalInfo()
  }

  getAdditionalInfoItemValue = (id) => {
    const item = this.additional_info.find(item => item.id === id)
    if (item) {
      return item.input
    }
    return null
  }

  @action deleteAdditionalInfoItem = (id) => {
    this.additional_info = this.additional_info.filter(item => item.id !== id)
    this.saveAdditionalInfo()
  }

  @action updateAdditionalInfoItem = (id, field, value) => {
    const index = this.findAdditionalInfoIndex(id)

    if (index === -1) {
      const definition = dataStore.userAdditionalInfoItems.find(item => item.id === id)
      if (!definition) return

      this.additional_info.push({
        id: definition.id,
        order: this.additional_info.length + 1,
        info_item: definition.id,
        label: definition.label,
        type: definition.type,
        input: {
          ...ADDITIONAL_INFO_DEFAULT_INPUT[definition.type],
          [field]: value
        }
      })
    } else {
      const item = this.additional_info[index]
      this.additional_info[index] = {
        ...item,
        input: {
          ...item.input,
          [field]: value
        }
      }
    }

    this.debouncedSaveAdditionalInfo(id)
  }

  @action updateAdditionalInfoItemType = (id, type, label) => {
    const index = this.additional_info.findIndex(item => item.id === id)
    if (index === -1) return

    const item = this.additional_info[index]
    if (item.type === type) return

    this.additional_info[index] = {
      ...item,
      type,
      label,
      input: {
        ...ADDITIONAL_INFO_DEFAULT_INPUT[type],
        title: item.input ? item.input.title : null
      }
    }
    this.debouncedSaveAdditionalInfo(id)
  }

  @action updateNotes = (value) => {
    this.notes = value
    this.debouncedSaveAdditionalInfo()
  }

  debouncedSaveAdditionalInfo = debounce((id) => {
    this.saveAdditionalInfo(id)
  }, 500)

  @action updateContactDetail = (field, value) => {
    this.contact_detail = { ...this.contact_detail, [field]: value }
    this.debouncedSaveContactDetail()
  }

  debouncedSaveContactDetail = debounce(() => {
    this.saveContactDetail()
  }, 800)

  @action sortAdditionalInfoItem (event) {
    const sorted_items = move(this.additional_info, event)
    this.additional_info = sorted_items
  }

  @catchError async saveAdditionalInfo (id) {
    const data = this.additional_info.find(item => item.info_item === id)
    await api.postGeopolygonAdditionalInfo(this.id, data)
  }

  @catchError async saveContactDetail () {
    await api.postGeopolygonContactDetail(this.id, this.contact_detail)
  }

  @catchError
  async rename (name) {
    if (this.name === this.renameForm.name) {
      return
    }

    // Exclude this geopolygon itself - by the time rename() runs, the caller
    // has usually already set this.name = name, so a plain {name} match would
    // find itself in its own target group's list and false-positive as a dupe.
    const siblingGeopolygons = selectionStore.data && selectionStore.data.group && selectionStore.data.group.geopolygons
    if (siblingGeopolygons && _.find(siblingGeopolygons, geo => geo !== this && geo.name === name)) {
      this.error = i18next.t('GLOBAL.DUPLICATE_ELEMENT_NAME', { element: i18next.t('GLOBAL.GEOPOLYGON') })
      return
    }

    await api.patchGeopolygon(this.id, { name })
  }

  @catchError
  async updateTags (tagId) {
    runInAction(() => {
      this.tag_ids.push(tagId)
    })
    await api.postGeopolygonTags(this.id, { tag_ids: [tagId] })
  }

  @catchError
  async deleteTag (tagId) {
    runInAction(() => {
      this.tag_ids = this.tag_ids.filter(id => tagId !== id)
    })
    await api.deleteGeoPolygonTag(this.id, tagId)
  }

  @catchError
  async updateStatus (shapeStatus) {
    runInAction(() => {
      this.status = shapeStatus.id
    })
    await api.patchGeopolygonStatus(this.id, { shape_status: shapeStatus.id })
  }

  @catchError
  async updateShape (newShape) {
    const wkt = new Wkt.Wkt()
    wkt.read(JSON.stringify(newShape.geometry))

    try {
      const result = await api.patchGeopolygon(this.id, { the_geom: wkt.write() })
      this.updateGeoJSON(result.data.polygon)
      return result.data
    } catch (error) {
      selectionStore.clear()
    }
  }

  updateGeoJSON (polygon) {
    this.sidebarCache.clear()
    this.computeGeoJSON(polygon)
    this.analysis = new RegionAnalysis()
    this.loadGeopolygonData()
  }

  @catchError
  async copy (targetGroupId) {
    await api.postCopyRegion(this.id, { target_group_id: targetGroupId })
  }

  @catchError
  async deleteGeopolygon () {
    await api.deleteGeopolygon(this.id)
  }

  bounds () {
    return geoJSONBounds(this.geoJSON)
  }

  get selection () {
    this.error = null
    return {
      item: this,
      type: selectionTypes.GEOPOLYGON
    }
  }

  async loadGeometryAnalysis (taskId, attempt = 0, cancelToken = null) {
    if (!taskId) return
    if (cancelToken && cancelToken.reason) return // Abort early if already cancelled

    try {
      const config = cancelToken ? { cancelToken } : {}
      const res = await api.getPopulationTaskPolling(taskId, config)
      const data = res.data
      if (data.status === 'PENDING') {
        // retry logic here
        console.log('Analysis is still processing. Retrying...')
        this.handleGoemetryAnalysisPending(taskId, attempt, cancelToken)
      }
      if (data.status === 'COMPLETE') {
        console.log('Analysis complete. Updating geopolygon data.')
        this.handleGeometryAnalysisComplete(data.result[0])
      }
      if (data.status === 'FAILED' || data.status === 'ERROR') {
        this.handleGeometryAnalysisFailed()
      }
    } catch (err) {
      if (!axios.isCancel(err)) {
        console.warn('Error in geometry analysis polling:', err)
      }
      this.handleGeometryAnalysisFailed()
    }
  }

  handleGoemetryAnalysisPending (taskId, attempt, cancelToken) {
    this.analysis.loading = true
    this.retryLoadGeometryAnalysis(attempt, taskId, cancelToken)
  }

  handleGeometryAnalysisFailed () {
    this.analysis.loading = false
    uiStore.setProcessing(false)
    console.error('Failed to load geometry analysis after multiple attempts.')
  }

  retryLoadGeometryAnalysis (attempt, taskId, cancelToken) {
    const delay = Math.min(1000 * Math.pow(2, attempt), 5000)
    if (attempt < 32) {
      setTimeout(() => {
        if (cancelToken && cancelToken.reason) return
        this.loadGeometryAnalysis(taskId, attempt + 1, cancelToken)
      }, delay)
    } else {
      this.handleGeometryAnalysisFailed()
    }
  }

  handleGeometryAnalysisComplete (population_datas) {
    this.analysis.load(population_datas, this.geoJSON)
    uiStore.setProcessing(false)
    if (selectionStore.item === this && rightSidebarStore) {
      const { getShapePopulationFetchOptions } = require('../utils')
      rightSidebarStore.fetchDataForSinglePolygon(
        this.id,
        this.name,
        getShapePopulationFetchOptions(this),
        null,
        true
      )
    }
  }

  async loadGeopolygonData (setProcessing, cancelToken = null) {
    const config = cancelToken ? { cancelToken } : {}

    runInAction(() => {
      if (rightSidebarStore) rightSidebarStore.pyramidLoading = true
    })

    try {
      const lsRes = await api.getGeopolygon(this.id, {
        ...config,
        headers: {
          ...(config.headers || {}),
          'Ds-Name': 'landscan',
          'Ds-Year': '2016'
        }
      })
      const lsData = lsRes && lsRes.data ? lsRes.data : null
      if (!lsData) return

      if (!_.isEmpty(lsData.buffer)) {
        selectionStore.setBufferForEditableShape(lsData.buffer)
      }
      if (lsData.task_id) {
        this.loadGeometryAnalysis(lsData.task_id, 0, cancelToken)
        return
      }

      const lsPd = lsData.population_datas
      const worldpopExists = Boolean(lsData.worldpop_exists)

      if (worldpopExists) {
        try {
          const wpRes = await api.getGeopolygon(this.id, {
            ...config,
            headers: {
              ...(config.headers || {}),
              'Ds-Name': 'worldpop',
              'Ds-Year': '2024'
            }
          })
          const wpData = wpRes && wpRes.data ? wpRes.data : null
          const wpPd = wpData && wpData.population_datas
          this.population_datas = mergePopulationDatas(this.population_datas || lsPd, wpPd) || lsPd || wpPd
        } catch (wpErr) {
          console.warn('Failed to fetch worldpop data for shape', wpErr)
          this.population_datas = mergePopulationDatas(this.population_datas, lsPd) || lsPd
        }
        runInAction(() => {
          if (rightSidebarStore) {
            rightSidebarStore.worldpopAvailable = true
            rightSidebarStore.worldpopLoading = false
          }
        })
      } else {
        this.population_datas = mergePopulationDatas(this.population_datas, lsPd) || lsPd
        runInAction(() => {
          if (rightSidebarStore) {
            rightSidebarStore.worldpopAvailable = false
            rightSidebarStore.worldpopLoading = true
            rightSidebarStore.startWorldpopPolling({ geoPolygonId: this.id, scope: 'standalone' })
          }
        })
      }

      if (rightSidebarStore && rightSidebarStore.singlePolygonData && String(rightSidebarStore.singlePolygonId) === String(this.id)) {
        rightSidebarStore.singlePolygonData.population_datas = this.population_datas
      }
      this.analysis.load(this.pickPopulationDataForCurrentYear(this.population_datas), this.geoJSON)
    } catch (e) {
      if (!axios.isCancel(e)) {
        console.warn('Error fetching geopolygon data:', e)
      }
    } finally {
      runInAction(() => {
        if (rightSidebarStore) rightSidebarStore.pyramidLoading = false
        if (setProcessing) uiStore.setProcessing(false)
      })
    }
  }

  reloadAnalysisForCurrentYear () {
    const picked = this.pickPopulationDataForCurrentYear(this.population_datas)
    this.analysis = new RegionAnalysis()
    if (picked) {
      this.analysis.load(picked, this.geoJSON)
    } else {
      this.loadGeopolygonData()
    }
  }

  async exportExcel () {
    await this.analysis.exportExcel(this.name)
  }

  async exportCSV () {
    await this.analysis.exportCSV(this.name)
  }

  @catchError async exportKml (kmlFormat) {
    const response = await api.getRegionKML(this.id, !kmlFormat, { responseType: 'blob' })
    downloadKML(this.name, response.data, kmlFormat)
  }

  @computed
  get smoothFactor () {
    return smoothFactor(this.geoJSON)
  }

  @computed get color () {
    return this.color_id
  }

  @computed get baseColor () {
    // The geometry's own color, ignoring any status assignment - used for the
    // fill when a status is shown as an outline rather than a full fill.
    const item = dataStore.markerColors.find(item => item.id === this.color_id)
    return item ? item.active_color : defaultPolygonColor
  }

  @computed get getColor () {
    // Existing assignments override the geometry color even while assignment UI is hidden.
    const status = dataStore.shapeStatuses.find(item => item.id === this.status)
    if (status) return status.color
    return this.baseColor
  }
}

export default Geopolygon
